import type { EventKind } from "./form-values";
import type { ClinicId, PatientId, ProviderId } from "./types";

export type CreateSource =
  | "slot"
  | "drag"
  | "keyboard"
  | "preview"
  | "toolbar"
  | "findNext"
  | "checkout";

/**
 * A new appointment being started from the scheduler. The appointment form (next step)
 * reads it to seed provider, clinic, date and time, the way the old grid's double-click
 * passes staff, clinic and start (`day-view-time-table-cell.component.tsx`).
 */
export interface CreateDraft {
  /** `YYYY-MM-DD`. */
  date: string;
  /** Minutes from midnight; `null` when opened without a time (toolbar, N). */
  start: number | null;
  end: number | null;
  providerId: ProviderId | null;
  clinicId: ClinicId | null;
  source: CreateSource;
  /** Set when the draft started from a dedicated block — see `block-booking.ts`. */
  appointmentTypeId?: number | null;
  scheduledBlockId?: number | null;
  /** The block occurrence the draft started from (ENG-2194): `block-booking.ts`. */
  blockInstance?: {
    eventId: string;
    startDate: string;
    endDate: string;
  } | null;
  allowOverlap?: boolean;
  /**
   * What the form opens as. Absent means an appointment. Manage Work Shifts sets
   * `"workShift"`, as the old grid's add does while `workShiftViewMode` is on
   * (`editing-state.component.tsx:90-106`).
   */
  kind?: EventKind;
  /**
   * Find Next Available's optional patient, which rides into the seeded form
   * (`find-next-available-dialog.component.tsx:333`).
   */
  patient?: { id: PatientId; label: string } | null;
}

/**
 * Where a new work shift picked on the grid ends. A click seeds an hour whatever the
 * slot size, as the old mode's add does (`editing-state.component.tsx:90-106`); a drag
 * keeps the length it was dragged to. Never past midnight.
 */
export function workShiftDraftEnd(
  start: number,
  pickedEnd: number,
  source: CreateSource
): number {
  if (source === "drag") return pickedEnd;
  return Math.min(24 * 60, start + 60);
}
